<template>
	<Drawer
		:pt="passThrough"
		:show-close-icon="false"
		:visible="visible"
		@show="onDrawerShow"
		@update:visible="$emit('update:visible', $event)"
		position="bottom"
	>
		<template #header>
			<div
				@touchend="onDragEnd"
				@touchmove.passive="onDragMove"
				@touchstart.passive="onDragStart"
				class="bottomSheet__grab"
			>
				<span class="bottomSheet__grip" />
				<span
					class="bottomSheet__title"
					v-if="title"
				>
					{{ title }}
				</span>
			</div>
			<div
				class="bottomSheet__headerAction"
				v-if="$slots.headerAction"
			>
				<slot name="headerAction" />
			</div>
		</template>
		<div
			class="bottomSheet__content"
			ref="contentRef"
		>
			<slot />
		</div>
	</Drawer>
</template>

<script lang="ts">
import { PropType, computed, defineComponent, nextTick, ref, watch } from 'vue';

import Drawer from 'primevue/drawer';

const SHEET_DISMISS_THRESHOLD_IN_PIXELS = 90;

const SHEET_DISMISS_ANIMATION_IN_MILLISECONDS = 200;

const DISMISS_REJECTION_RESET_IN_MILLISECONDS = 150;

const SCROLL_EDGE_EPSILON_IN_PIXELS = 1;

export default defineComponent({
	components: { Drawer },
	emits: ['update:visible'],
	name: 'BottomSheet',
	props: {
		/** Deaktiviert die Scroll-Fade-Masken, z. B. wenn ein sticky Element oben liegt. */
		scrollFadeDisabled: { default: false, required: false, type: Boolean },
		title: { default: null, required: false, type: String as PropType<null | string> },
		visible: { required: true, type: Boolean },
	},
	setup(props, { emit, slots }) {
		const contentRef = ref<HTMLElement | null>(null);
		const isScrolledFromTop = ref(false);
		const canScrollFurtherDown = ref(false);
		let observedScrollElement: HTMLElement | null = null;

		const updateScrollEdges = (): void => {
			if (observedScrollElement === null) {
				return;
			}

			isScrolledFromTop.value = observedScrollElement.scrollTop > 0;
			canScrollFurtherDown.value =
				observedScrollElement.scrollTop + observedScrollElement.clientHeight <
				observedScrollElement.scrollHeight - SCROLL_EDGE_EPSILON_IN_PIXELS;
		};

		const syncScrollEdgeTracking = (): void => {
			const scrollElement = contentRef.value?.parentElement ?? null;

			if (scrollElement !== observedScrollElement) {
				observedScrollElement?.removeEventListener('scroll', updateScrollEdges);
				observedScrollElement = scrollElement;
				scrollElement?.addEventListener('scroll', updateScrollEdges, { passive: true });
			}

			updateScrollEdges();
		};

		const onDrawerShow = (): void => {
			void nextTick(() => syncScrollEdgeTracking());
		};

		watch(
			() => props.visible,
			(visible) => {
				if (!visible) {
					isScrolledFromTop.value = false;
					canScrollFurtherDown.value = false;
				}
			}
		);

		const passThrough = computed(() => ({
			content: {
				class: [
					'bottomSheet__drawerContent',
					props.scrollFadeDisabled ? 'bottomSheet__drawerContent--no-fade' : '',
					isScrolledFromTop.value ? 'bottomSheet__drawerContent--fade-top' : '',
					canScrollFurtherDown.value ? 'bottomSheet__drawerContent--fade-bottom' : '',
				]
					.filter((cssClass) => cssClass !== '')
					.join(' '),
			},
			header: {
				class:
					slots.headerAction === undefined
						? 'bottomSheet__drawerHeader'
						: 'bottomSheet__drawerHeader bottomSheet__drawerHeader--withAction',
			},
			root: { class: 'bottomSheet__drawer' },
		}));

		let dragStartY = 0;
		let dragPanel: HTMLElement | null = null;
		let isDragging = false;

		const onDragStart = (event: TouchEvent): void => {
			dragStartY = event.touches[0].clientY;
			dragPanel = (event.currentTarget as HTMLElement).closest<HTMLElement>('.p-drawer');
			isDragging = dragPanel !== null;
			if (dragPanel) {
				dragPanel.style.transition = 'none';
			}
		};

		const onDragMove = (event: TouchEvent): void => {
			if (!isDragging || !dragPanel) {
				return;
			}
			const distanceDragged = event.touches[0].clientY - dragStartY;
			dragPanel.style.transform = distanceDragged > 0 ? `translateY(${distanceDragged}px)` : '';
		};

		const resetPanelDismissStyles = (panel: HTMLElement): void => {
			panel.style.transition = `transform ${SHEET_DISMISS_ANIMATION_IN_MILLISECONDS}ms ease`;
			panel.style.transform = '';
		};

		const scheduleDismissRejectionRecovery = (panel: HTMLElement): void => {
			window.setTimeout((): void => {
				if (props.visible) {
					resetPanelDismissStyles(panel);
				}
			}, DISMISS_REJECTION_RESET_IN_MILLISECONDS);
		};

		const onDragEnd = (event: TouchEvent): void => {
			if (!isDragging || !dragPanel) {
				return;
			}
			const panel = dragPanel;
			const distanceDragged = event.changedTouches[0].clientY - dragStartY;
			isDragging = false;
			dragPanel = null;
			panel.style.transition = `transform ${SHEET_DISMISS_ANIMATION_IN_MILLISECONDS}ms ease`;
			if (distanceDragged > SHEET_DISMISS_THRESHOLD_IN_PIXELS) {
				panel.style.transform = 'translateY(100%)';
				window.setTimeout(() => {
					emit('update:visible', false);
					scheduleDismissRejectionRecovery(panel);
				}, SHEET_DISMISS_ANIMATION_IN_MILLISECONDS);
			} else {
				panel.style.transform = '';
			}
		};

		return {
			contentRef,
			onDragEnd,
			onDragMove,
			onDragStart,
			onDrawerShow,
			passThrough,
		};
	},
});
</script>

<style lang="scss">
.p-drawer-bottom .bottomSheet__drawer.p-drawer {
	border-top-left-radius: 16px;
	border-top-right-radius: 16px;
	color: var(--p-text-color);
	height: min(56dvh, calc(100dvh - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) - 24px));
	overflow: hidden;
}

.body--dark .bottomSheet__drawer.p-drawer {
	background: var(--p-content-background);
	border-color: var(--p-surface-700);
}

.bottomSheet__drawerHeader.p-drawer-header {
	background: transparent;
	padding: 8px 16px 6px;
	position: relative;
}

.bottomSheet__headerAction {
	align-items: center;
	bottom: 4px;
	display: inline-flex;
	gap: 0.25rem;
	position: absolute;
	right: 12px;
}

.bottomSheet__grab {
	align-items: center;
	cursor: grab;
	display: flex;
	flex-direction: column;
	gap: 8px;
	touch-action: none;
	width: 100%;
}

.bottomSheet__grip {
	background: var(--p-content-border-color);
	border-radius: 999px;
	height: 4px;
	width: 38px;
}

.bottomSheet__title {
	color: var(--p-text-color);
	font-size: 15px;
	font-weight: 600;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.bottomSheet__drawerHeader--withAction {
	--bottom-sheet-header-action-reserve: 5rem;
}

.bottomSheet__drawerHeader--withAction .bottomSheet__title {
	max-width: calc(100% - 2 * var(--bottom-sheet-header-action-reserve));
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.bottomSheet__drawerContent.p-drawer-content {
	min-height: 0;
	overflow-y: auto;
	padding: 4px 8px calc(env(safe-area-inset-bottom, 0px) + 12px);
}

.bottomSheet__drawerContent--fade-top:not(.bottomSheet__drawerContent--no-fade) {
	mask-image: linear-gradient(to bottom, transparent 0, #000 16px);
	-webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 16px);
}

.bottomSheet__drawerContent--fade-bottom:not(.bottomSheet__drawerContent--no-fade) {
	mask-image: linear-gradient(to bottom, #000 calc(100% - 16px), transparent 100%);
	-webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 16px), transparent 100%);
}

.bottomSheet__drawerContent--fade-top.bottomSheet__drawerContent--fade-bottom:not(
		.bottomSheet__drawerContent--no-fade
	) {
	mask-image: linear-gradient(to bottom, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%);
	-webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%);
}

.bottomSheet__content {
	display: flex;
	flex-direction: column;
}

.bottomSheet__content .mobile-chip-popover__option {
	font-size: 15px;
	padding: 13px 12px;
	white-space: normal;
	width: 100%;
}
</style>
